import type {BadgeDTO} from '@/ts/types/dto/badge-dto';

const BADGE_EARNED_KEY = 'badgeEarned';
const REDIRECT_INTENT_KEY = 'redirectIntent';
const CHALLENGE_EXPIRED_KEY = 'challengeExpired';
const COMPLETE_SIGN_UP_KEY = 'completeSignUp';
const CONSENT_RETURN_PATH_KEY = 'consentReturnPath';

/** Typed wrapper around sessionStorage for badge and redirect intent data */
export class SessionStorage {
  public get badgeEarned(): BadgeDTO | null {
    const badge = sessionStorage.getItem(BADGE_EARNED_KEY);
    if (badge) {
      try {
        return JSON.parse(badge) as BadgeDTO;
      } catch {
        return null;
      }
    }
    return null;
  }

  public set badgeEarned(value: BadgeDTO | null) {
    if (value) {
      sessionStorage.setItem(BADGE_EARNED_KEY, JSON.stringify(value));
    } else {
      sessionStorage.removeItem(BADGE_EARNED_KEY);
    }
  }

  public get redirectIntent(): string | null {
    return sessionStorage.getItem(REDIRECT_INTENT_KEY);
  }

  public set redirectIntent(value: string | null) {
    if (value) {
      sessionStorage.setItem(REDIRECT_INTENT_KEY, value);
    } else {
      sessionStorage.removeItem(REDIRECT_INTENT_KEY);
    }
  }

  /**
   * Flags that a challenge just auto-expired, carrying the signal across the session→landing
   * navigation. The BE clears its pending-acknowledgment flag on the first read, so the landing
   * page can no longer re-detect the expiry itself and relies on this instead.
   */
  /**
   * Where the user was headed when the legal consent gate sent them to the consent page, so they
   * land there once they consent rather than on home (IPD-3536). Session-scoped, like the sign-in
   * intent, and it survives the reload a language change triggers.
   */
  public get consentReturnPath(): string | null {
    return sessionStorage.getItem(CONSENT_RETURN_PATH_KEY);
  }

  public set consentReturnPath(value: string | null) {
    if (value) {
      sessionStorage.setItem(CONSENT_RETURN_PATH_KEY, value);
    } else {
      sessionStorage.removeItem(CONSENT_RETURN_PATH_KEY);
    }
  }

  public get challengeExpired(): boolean {
    return sessionStorage.getItem(CHALLENGE_EXPIRED_KEY) === 'true';
  }

  public set challengeExpired(value: boolean) {
    if (value) {
      sessionStorage.setItem(CHALLENGE_EXPIRED_KEY, 'true');
    } else {
      sessionStorage.removeItem(CHALLENGE_EXPIRED_KEY);
    }
  }

  /**
   * Flags that the user authenticated (e.g. with Google) but has no Balm account yet, so the
   * sign-up page runs its shortened variant instead of the full wizard (IPD-2844). Session-scoped
   * on purpose: it must not survive the tab, or a later visit would resume a sign-up that the
   * user already abandoned.
   */
  public get completeSignUp(): boolean {
    return sessionStorage.getItem(COMPLETE_SIGN_UP_KEY) === 'true';
  }

  public set completeSignUp(value: boolean) {
    if (value) {
      sessionStorage.setItem(COMPLETE_SIGN_UP_KEY, 'true');
    } else {
      sessionStorage.removeItem(COMPLETE_SIGN_UP_KEY);
    }
  }
}
